import React, { useState, useEffect } from 'react';
import { Row, Col } from 'react-bootstrap';
import { fetchManagerDashboardStats, fetchManagerChatLogs, ChatLogListItem } from 'src/services/api';
import { AddNewUserModal } from 'src/pages/manager/AddNewUserModal';
import { useChatSession } from 'src/contexts/ChatSessionContext';
import { useTimezoneFormatting } from 'src/hooks/useTimezoneFormatting';
import Preloader from 'src/components/Preloder';
import totalUsers from 'src/assets/icons/totalUsers.png';
import totalQueries from 'src/assets/icons/totalQueries.png';
import activeWorkers from 'src/assets/icons/activeWorkers.png';

interface ManagerDashboardStats {
    total_workers: number;
    total_queries: number;
    active_workers: number;
}

type ActivityChat = Pick<ChatLogListItem, 'id' | 'title' | 'date'>;

const ManagerDashboard: React.FC = () => {
    const { refreshTrigger } = useChatSession();
    const { formatToLocal } = useTimezoneFormatting();
    const [stats, setStats] = useState<ManagerDashboardStats>({
        total_workers: 0,
        total_queries: 0,
        active_workers: 0,
    });
    const [chatSessions, setChatSessions] = useState<ActivityChat[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [addUserModalOpen, setAddUserModalOpen] = useState(false);

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                setError(null);

                // Fetch dashboard stats and top 4 past chats (chat logs) in parallel
                const [statsData, chatLogsData] = await Promise.all([
                    fetchManagerDashboardStats(),
                    fetchManagerChatLogs({ page: 1, pageSize: 4 })
                ]);

                setStats(statsData);
                const items: ChatLogListItem[] = chatLogsData.items || [];
                setChatSessions(items.map(i => ({ id: i.id, title: i.title, date: i.date })));
            } catch (err) {
                setError(err instanceof Error ? err.message : 'Failed to load dashboard data');
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [refreshTrigger]);

    // Show preloader while data is loading
    if (loading) {
        return <Preloader />;
    }

    return (
        <div className="flex flex-1 flex-col min-h-0 min-w-0 overflow-hidden">
            {/* Error Message */}
            {error && (
                <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
                    <div className="d-flex">
                        <div className="flex-shrink-0">
                            <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                            </svg>
                        </div>
                        <div className="ml-3">
                            <h3 className="text-sm font-medium text-red-800">
                                Error loading dashboard data
                            </h3>
                            <div className="mt-2 text-sm text-red-700">
                                {error}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Stats */}
            <Row>
                <Col xs={12} sm={6} lg={4} className="mb-4">
                    <StatCard title={"Total Workers Under You"} description={"Field workers assigned to you"} value={error ? '--' : String(stats.total_workers)} iconSrc={totalUsers} />
                </Col>
                <Col xs={12} sm={6} lg={4} className="mb-4">
                    <StatCard title={"Total Queries by Your Workers"} description={"Questions asked by your workers"} value={error ? '--' : String(stats.total_queries)} iconSrc={totalQueries} />
                </Col>
                <Col xs={12} sm={6} lg={4} className="mb-4">
                    <StatCard title={"Active Workers"} description={"Logged in within last 7 days"} value={error ? '--' : String(stats.active_workers)} iconSrc={activeWorkers} />
                </Col>
            </Row>

            {/* Activity + Quick Actions */}
            <Row>
                {/* Latest Activities */}
                <Col lg={6}>
                    <div className="db-big-card">
                        <div className="db-big-card-header">
                            <img src="/icons/latestFeed.png" alt="Latest feed" />
                            <span className="text-14-bold grey-800">
                                Latest Activities Feed
                            </span>
                        </div>
                        <div className="db-big-card-body">
                            {chatSessions.length > 0 ? (
                                chatSessions.map((session) => (
                                    <ActivityItem
                                        key={session.id}
                                        title={session.title}
                                        uploadedOn={session.date}
                                        formatDate={(dateString: string) => {
                                            // Ensure timestamp is treated as UTC
                                            const normalizedTimestamp = dateString.endsWith('Z') ? dateString : dateString + 'Z';
                                            const date = new Date(normalizedTimestamp);
                                            return date.toLocaleDateString('en-GB', {
                                                year: 'numeric',
                                                month: '2-digit',
                                                day: '2-digit'
                                            });
                                        }}
                                    />
                                ))
                            ) : (
                                <div className="text-center text-muted py-5">No past chats found</div>
                            )}
                        </div>
                    </div>
                </Col>

                {/* Quick Actions */}
                <Col lg={6}>
                    <div className="db-big-card">
                        <div className="db-big-card-header">
                            <img src="/icons/quickActions.png" alt="Quick actions" />
                            <span className="text-14-bold grey-800">
                                Quick Actions
                            </span>
                        </div>
                        <div className="db-big-card-body">
                            {/* Add New User */}
                            <div className="quick-action-item" onClick={() => setAddUserModalOpen(true)}>
                                <i className="iconoir-user-plus"></i>
                                <div>
                                    <h4 className="grey-800 mb-1">Add New User</h4>
                                    <p className="text-14-reg grey-500 mb-0">Create new user account</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </Col>
            </Row>

            {/* Add New User Modal */}
            <AddNewUserModal
                show={addUserModalOpen}
                onClose={() => setAddUserModalOpen(false)}
                onCreated={(newWorker) => {
                    // Refresh stats after creating a new worker
                    const fetchData = async () => {
                        try {
                            const statsData = await fetchManagerDashboardStats();
                            setStats(statsData);
                        } catch (err) {
                            // Silently handle refresh errors
                        }
                    };
                    fetchData();
                }}
            />
        </div>
    );
};

function ActivityItem({ title, uploadedOn, formatDate }: { title: string; uploadedOn?: string; formatDate: (dateString: string) => string }) {
    return (
        <>
            <div className="latest-activity-item">
                <div className="d-flex justify-content-between mb-3">
                    <p className="text-16-bold grey-900">My Past Chat</p>
                    <p className="text-12-reg grey-900 d-flex align-items-center">
                        <svg width="16" height="17" viewBox="0 0 16 17" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <g clip-path="url(#clip0_2416_277)">
                                <path d="M14 8.25781C14.3125 8.47135 14.5911 8.71354 14.8359 8.98438C15.0807 9.25521 15.2917 9.55208 15.4688 9.875C15.6458 10.1979 15.776 10.5391 15.8594 10.8984C15.9427 11.2578 15.9896 11.625 16 12C16 12.6198 15.8828 13.2031 15.6484 13.75C15.4141 14.2969 15.0911 14.7734 14.6797 15.1797C14.2682 15.5859 13.7917 15.9062 13.25 16.1406C12.7083 16.375 12.125 16.4948 11.5 16.5C11.026 16.5 10.5677 16.4297 10.125 16.2891C9.68229 16.1484 9.27604 15.9453 8.90625 15.6797C8.53646 15.4141 8.20833 15.0964 7.92188 14.7266C7.63542 14.3568 7.41406 13.9479 7.25781 13.5H1V1.5H3V0.5H4V1.5H11V0.5H12V1.5H14V8.25781ZM2 2.5V4.5H13V2.5H12V3.5H11V2.5H4V3.5H3V2.5H2ZM7.02344 12.5C7.00781 12.3385 7 12.1719 7 12C7 11.5521 7.0625 11.1172 7.1875 10.6953C7.3125 10.2734 7.5026 9.875 7.75781 9.5H7V8.5H8V9.17188C8.21354 8.90625 8.45052 8.67188 8.71094 8.46875C8.97135 8.26562 9.2526 8.09115 9.55469 7.94531C9.85677 7.79948 10.1719 7.6901 10.5 7.61719C10.8281 7.54427 11.1615 7.50521 11.5 7.5C12.0208 7.5 12.5208 7.58594 13 7.75781V5.5H2V12.5H7.02344ZM11.5 15.5C11.9844 15.5 12.4375 15.4089 12.8594 15.2266C13.2812 15.0443 13.651 14.7943 13.9688 14.4766C14.2865 14.1589 14.5365 13.7891 14.7188 13.3672C14.901 12.9453 14.9948 12.4896 15 12C15 11.5156 14.9089 11.0625 14.7266 10.6406C14.5443 10.2188 14.2943 9.84896 13.9766 9.53125C13.6589 9.21354 13.2891 8.96354 12.8672 8.78125C12.4453 8.59896 11.9896 8.50521 11.5 8.5C11.0156 8.5 10.5625 8.59115 10.1406 8.77344C9.71875 8.95573 9.34896 9.20573 9.03125 9.52344C8.71354 9.84115 8.46354 10.2109 8.28125 10.6328C8.09896 11.0547 8.00521 11.5104 8 12C8 12.4844 8.09115 12.9375 8.27344 13.3594C8.45573 13.7812 8.70573 14.151 9.02344 14.4688C9.34115 14.7865 9.71094 15.0365 10.1328 15.2188C10.5547 15.401 11.0104 15.4948 11.5 15.5ZM12 11.5H13.5V12.5H11V9.5H12V11.5ZM3 8.5H4V9.5H3V8.5ZM5 8.5H6V9.5H5V8.5ZM5 6.5H6V7.5H5V6.5ZM3 10.5H4V11.5H3V10.5ZM5 10.5H6V11.5H5V10.5ZM8 7.5H7V6.5H8V7.5ZM10 7.5H9V6.5H10V7.5ZM12 7.5H11V6.5H12V7.5Z" fill="black" />
                            </g>
                            <defs>
                                <clipPath id="clip0_2416_277">
                                    <rect width="16" height="16" fill="white" transform="translate(0 0.5)" />
                                </clipPath>
                            </defs>
                        </svg>
                        <span className="ms-2">{uploadedOn ? formatDate(uploadedOn) : ''}</span>
                    </p>
                </div>
                <div className="activity-item-content">
                    {title}
                </div>
            </div>
        </>
    );
}


function StatCard({ title, value, description, iconSrc }: { title: string; value: string; description: string; iconSrc: string }) {
    return (
        <div className="dashboard-stat-card">
            <div className="d-flex flex-column h-100 justify-content-between">
                <div className="d-flex justify-content-between align-items-center mb-2">
                    <h6 className="text-16-reg grey-600 mt-1 w-100">{title}</h6>
                    <div className="stat-card-icon">
                        <img src={iconSrc} alt="" style={{ width: '16px', height: '16px' }} />
                    </div>
                </div>
                <h1 className="grey-900 mb-2">{value}</h1>
                <p className="text-13-reg grey-700 mb-0">{description}</p>
            </div>
        </div>
    );
}

export default ManagerDashboard;
